// ESC/POS printer helper — Web Bluetooth (BLE) + Web USB
// 58mm = 32 chars, 80mm = 48 chars
// Mendukung banyak varian modul BLE thermal printer (Xprinter, Rongta, HPRT, dsb.)

const ESC = 0x1B, GS = 0x1D, LF = 0x0A;

// Daftar service+char umum untuk BLE thermal printer 58/80mm
// Urutan penting: pertama yang cocok akan dipakai.
const BT_TARGETS = [
  { svc: "000018f0-0000-1000-8000-00805f9b34fb", chr: "00002af1-0000-1000-8000-00805f9b34fb" }, // paling umum
  { svc: "0000ff00-0000-1000-8000-00805f9b34fb", chr: "0000ff02-0000-1000-8000-00805f9b34fb" },
  { svc: "0000ffe0-0000-1000-8000-00805f9b34fb", chr: "0000ffe1-0000-1000-8000-00805f9b34fb" },
  { svc: "49535343-fe7d-4ae5-8fa9-9fafd205e455", chr: "49535343-8841-43f4-a8d4-ecbe34729bb3" },
  { svc: "e7810a71-73ae-499d-8c15-faa9aef0c3f2", chr: "bef8d6c9-9c21-4c9e-b632-bd58c1009f9f" },
];

// ---------- BLUETOOTH ----------
export async function connectBluetooth() {
  if (!navigator.bluetooth) throw new Error("Peramban tidak mendukung Web Bluetooth. Gunakan Chrome/Edge di Android atau desktop.");
  const optionalServices = BT_TARGETS.map(t => t.svc);
  let device;
  try {
    // acceptAllDevices supaya printer yg tidak mengiklankan service tetap muncul
    device = await navigator.bluetooth.requestDevice({
      acceptAllDevices: true,
      optionalServices,
    });
  } catch (e) {
    if (e.name === "NotFoundError") throw new Error("Tidak ada printer dipilih.");
    throw e;
  }
  const server = await device.gatt.connect();
  let matched = null;
  for (const t of BT_TARGETS) {
    try {
      const svc = await server.getPrimaryService(t.svc);
      const chr = await svc.getCharacteristic(t.chr);
      matched = { svc, chr, meta: t };
      break;
    } catch (_) { /* try next */ }
  }
  if (!matched) {
    try { device.gatt.disconnect(); } catch {}
    throw new Error("Printer tidak dikenali. Pastikan printer thermal ESC/POS 58/80mm menyala dan sudah pairing.");
  }
  device.addEventListener("gattserverdisconnected", () => {
    // eslint-disable-next-line no-console
    console.warn("BT printer terputus");
  });
  try { localStorage.setItem("rp_last_printer_bt", device.name || ""); } catch {}
  return { type: "bluetooth", name: device.name || "Bluetooth Printer", device, chr: matched.chr };
}

// ---------- USB ----------
export async function connectUSB() {
  if (!navigator.usb) throw new Error("Peramban tidak mendukung Web USB. Gunakan Chrome/Edge desktop.");
  const device = await navigator.usb.requestDevice({ filters: [] });
  await device.open();
  if (device.configuration === null) await device.selectConfiguration(1);
  let ifaceNum = 0, epOut = 0;
  for (const cfg of device.configurations) {
    for (const iface of cfg.interfaces) {
      for (const alt of iface.alternates) {
        for (const ep of alt.endpoints) {
          if (ep.direction === "out" && ep.type === "bulk") {
            ifaceNum = iface.interfaceNumber;
            epOut = ep.endpointNumber;
            break;
          }
        }
      }
    }
    if (epOut) break;
  }
  await device.claimInterface(ifaceNum);
  return { type: "usb", name: device.productName || "USB Printer", device, epOut };
}

// ---------- Write ----------
async function writeBytes(conn, bytes) {
  const CHUNK = 180;
  if (conn.type === "bluetooth") {
    // writeValueWithoutResponse lebih cepat kalau didukung, fallback ke writeValue
    const writer = conn.chr.writeValueWithoutResponse
      ? (b) => conn.chr.writeValueWithoutResponse(b)
      : (b) => conn.chr.writeValue(b);
    for (let i = 0; i < bytes.length; i += CHUNK) {
      await writer(bytes.slice(i, i + CHUNK));
      await new Promise(r => setTimeout(r, 25));
    }
  } else if (conn.type === "usb") {
    for (let i = 0; i < bytes.length; i += CHUNK) {
      await conn.device.transferOut(conn.epOut, bytes.slice(i, i + CHUNK));
    }
  }
}

// ---------- Text builder ----------
function textToBytes(str) { return new TextEncoder().encode(str); }
function center(text, w) { if (text.length >= w) return text.slice(0, w); return " ".repeat(Math.floor((w - text.length) / 2)) + text; }
function line(w) { return "-".repeat(w); }
function twoCol(l, r, w) {
  const rr = String(r);
  const space = Math.max(1, w - l.length - rr.length);
  return l + " ".repeat(space) + rr;
}

const DAY_ID = ["Minggu","Senin","Selasa","Rabu","Kamis","Jumat","Sabtu"];
const MONTH_ID = ["Jan","Feb","Mar","Apr","Mei","Jun","Jul","Agt","Sep","Okt","Nov","Des"];
function fmtDateID(iso) {
  if (!iso) return "-";
  const d = new Date(iso + "T00:00:00");
  if (isNaN(d)) return iso;
  return `${DAY_ID[d.getDay()]}, ${d.getDate()} ${MONTH_ID[d.getMonth()]} ${d.getFullYear()}`;
}

export function buildReceiptText(rental, settings, width = 32) {
  const fmt = (n) => "Rp" + Number(n || 0).toLocaleString("id-ID");
  const totalSize = settings.receipt_total_size || "sedang";
  const totalMark = totalSize === "besar" ? HUGE_MARK : totalSize === "kecil" ? SMALL_MARK : BIG_MARK;
  const L = [];
  L.push(center(settings.business_name || "RentalPro", width));
  if (settings.business_address) L.push(center(settings.business_address, width));
  L.push(center("WA: " + (settings.business_phone || ""), width));
  if (settings.receipt_show_socials !== false && settings.business_socials) L.push(center(settings.business_socials, width));
  if (settings.receipt_header_extra) L.push(center(settings.receipt_header_extra, width));
  L.push(line(width));
  L.push("No  : " + rental.code);
  L.push("Tgl : " + new Date(rental.created_at).toLocaleString("id-ID"));
  if (settings.receipt_show_kasir !== false) L.push("Kasir: " + (rental.created_by || "-"));
  if (settings.receipt_show_customer !== false) {
    L.push("Nama: " + rental.customer_name);
    if (rental.customer_whatsapp) L.push("WA  : " + rental.customer_whatsapp);
  }
  L.push(line(width));
  for (const it of rental.items) {
    L.push(it.product_name + (it.variant_name ? ` [${it.variant_name}]` : ""));
    L.push(twoCol(`  ${it.quantity} x ${fmt(it.daily_price)} x ${rental.days}h`, fmt(it.daily_price * it.quantity * rental.days), width));
  }
  L.push(line(width));
  L.push(twoCol("Subtotal", fmt(rental.subtotal), width));
  if (rental.discount_amount || rental.discount) {
    const dLabel = rental.discount_type === "persen" ? `Diskon (${rental.discount}%)` : "Diskon";
    L.push(twoCol(dLabel, "-" + fmt(rental.discount_amount || rental.discount), width));
  }
  if (rental.late_fee > 0) L.push(twoCol("Denda Telat", fmt(rental.late_fee), width));
  // Baris TOTAL ditandai untuk dicetak dengan font besar (ukuran diatur Owner)
  L.push(totalMark + twoCol("TOTAL", fmt(rental.total), Math.floor(width / 2)));
  if (rental.dp_amount > 0) {
    L.push(twoCol("DP Dibayar", fmt(rental.dp_amount), width));
    L.push(BIG_MARK + twoCol("SISA", fmt(rental.remaining), Math.floor(width / 2)));
  }
  if (settings.receipt_show_deposit !== false && rental.deposit) L.push(twoCol("Jaminan", fmt(rental.deposit), width));
  L.push("Ambil : " + fmtDateID(rental.start_date));
  if (settings.receipt_show_pickup_time !== false && rental.picked_up_at) {
    L.push("Jam Ambil: " + new Date(rental.picked_up_at).toLocaleString("id-ID"));
  }
  L.push("Kembali: " + fmtDateID(rental.end_date));
  if (settings.receipt_show_notes && rental.notes) { L.push(line(width)); L.push("Catatan: " + rental.notes); }
  L.push(line(width));
  if (settings.receipt_custom_note) L.push(center(settings.receipt_custom_note, width));
  if (settings.receipt_footer) L.push(center(settings.receipt_footer, width));
  if (settings.receipt_thank_you) L.push(center(settings.receipt_thank_you, width));
  return L.join("\n") + "\n\n\n";
}

// Penanda internal baris font besar (dibuang di preview teks/WA)
export const BIG_MARK = "\u0001";
export const HUGE_MARK = "\u0002";
export const SMALL_MARK = "\u0003";
export const stripBigMarks = (text) => text.split("\n").map(l => (l.startsWith(BIG_MARK) || l.startsWith(HUGE_MARK) || l.startsWith(SMALL_MARK)) ? l.slice(1) : l).join("\n");

// Print (text-only, aman & cepat untuk semua thermal printer 58/80mm)
export async function printReceipt(conn, rental, settings, width = 32) {
  const init = new Uint8Array([ESC, 0x40]); // reset
  const alignCenter = new Uint8Array([ESC, 0x61, 0x01]);
  const alignLeft = new Uint8Array([ESC, 0x61, 0x00]);
  const boldOn = new Uint8Array([ESC, 0x45, 0x01]);
  const boldOff = new Uint8Array([ESC, 0x45, 0x00]);
  const sizeBig = new Uint8Array([GS, 0x21, 0x11]); // double width + height
  const sizeHuge = new Uint8Array([GS, 0x21, 0x22]); // triple width + height (TOTAL)
  const dblOn = new Uint8Array([ESC, 0x47, 0x01]);  // double-strike (extra tebal)
  const dblOff = new Uint8Array([ESC, 0x47, 0x00]);
  const sizeNormal = new Uint8Array([GS, 0x21, 0x00]);
  const feed = new Uint8Array([LF, LF, LF]);
  const cut = new Uint8Array([GS, 0x56, 0x42, 0x00]);

  const text = buildReceiptText(rental, settings, width);
  const parts = [init, alignCenter, boldOn, textToBytes((settings.business_name || "RentalPro") + "\n"), boldOff, alignLeft];
  for (const ln of text.split("\n")) {
    if (ln.startsWith(HUGE_MARK)) {
      parts.push(alignCenter, boldOn, dblOn, sizeHuge, textToBytes(ln.slice(1) + "\n"), sizeNormal, dblOff, boldOff, alignLeft);
    } else if (ln.startsWith(BIG_MARK)) {
      parts.push(boldOn, sizeBig, textToBytes(ln.slice(1) + "\n"), sizeNormal, boldOff);
    } else if (ln.startsWith(SMALL_MARK)) {
      parts.push(boldOn, dblOn, textToBytes(ln.slice(1) + "\n"), dblOff, boldOff);
    } else {
      parts.push(textToBytes(ln + "\n"));
    }
  }
  parts.push(feed, cut);
  const total = parts.reduce((a, p) => a + p.length, 0);
  const bytes = new Uint8Array(total);
  let off = 0;
  for (const p of parts) { bytes.set(p, off); off += p.length; }
  await writeBytes(conn, bytes);
}
